import React, { useState, useEffect, useMemo } from 'react';
import { useAgentTeamStore, type WorkerStreamState, type WorkerStreamStatus } from '../../store/agentTeamStore';
import { ROLE_EMOJI_MAP } from '../../adapters/agentTeamAdapter';
import { Loader2, CheckCircle2, XCircle, ChevronDown, ChevronUp, Zap } from 'lucide-react';

const AGENT_LABELS: Record<string, string> = {
  explorer: '探索者',
  writer: '写作者',
  researcher: '研究者',
  analyst: '分析者',
  verifier: '验证者',
  reviewer: '审查者',
  planner: '规划者',
  executor: '执行者',
};

function getStatusIcon(status: WorkerStreamStatus) {
  switch (status) {
    case 'running':
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />;
    case 'completed':
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
    case 'failed':
      return <XCircle className="h-3.5 w-3.5 text-red-500" />;
  }
}

function getStatusBg(status: WorkerStreamStatus): string {
  switch (status) {
    case 'running':
      return 'border-blue-200 bg-blue-50/90';
    case 'completed':
      return 'border-green-200 bg-green-50/90';
    case 'failed':
      return 'border-red-200 bg-red-50/90';
  }
}

const ToolStepIcon: React.FC<{ status?: string }> = ({ status }) => {
  switch (status) {
    case 'executing':
      return <Loader2 className="h-3 w-3 animate-spin text-primary" />;
    case 'success':
      return <CheckCircle2 className="h-3 w-3 text-green-500" />;
    case 'failed':
      return <XCircle className="h-3 w-3 text-red-500" />;
    default:
      return <span className="h-3 w-3 rounded-full border border-muted-foreground/30" />;
  }
};

const WorkerStreamCard: React.FC<{ worker: WorkerStreamState; isExpanded: boolean }> = ({
  worker,
  isExpanded,
}) => {
  const agentName = AGENT_LABELS[worker.agentType] || worker.agentType;
  const agentEmoji =
    ROLE_EMOJI_MAP[worker.agentType as keyof typeof ROLE_EMOJI_MAP] || '🤖';

  const toolParts = worker.parts.filter(p => p.type === 'tool');
  const textContent = worker.streamingContent;

  if (!isExpanded) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5">
        {getStatusIcon(worker.status)}
        <span className="text-xs">
          {agentEmoji} {agentName}
        </span>
        {worker.status === 'running' && textContent && (
          <span className="text-xs text-muted-foreground truncate max-w-[120px]">
            {textContent.slice(-30)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`border rounded-lg ${getStatusBg(worker.status)} overflow-hidden`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/30">
        {getStatusIcon(worker.status)}
        <span className="text-sm font-medium">
          {agentEmoji} {agentName}
        </span>
        <span className="text-xs text-muted-foreground truncate flex-1">
          {worker.description.length > 60
            ? worker.description.slice(0, 60) + '...'
            : worker.description}
        </span>
      </div>

      <div className="px-3 py-2 space-y-1.5">
        {toolParts.length > 0 && (
          <div className="space-y-0.5">
            {toolParts.map((part, idx) => (
              <div
                key={part.tool_call_id || `${part.tool_name}-${idx}`}
                className="flex items-center gap-1.5"
              >
                <ToolStepIcon status={part.status} />
                <span className="text-xs text-foreground/80">{part.tool_name}</span>
                {part.status === 'executing' && (
                  <span className="inline-block w-1 h-3 ml-0.5 bg-blue-400 animate-pulse rounded-full" />
                )}
              </div>
            ))}
          </div>
        )}

        {worker.status === 'running' && textContent && (
          <div className="text-xs text-muted-foreground whitespace-pre-wrap font-mono bg-muted/40 rounded p-1.5 max-h-[80px] overflow-y-auto">
            {textContent.length > 300 ? '...' + textContent.slice(-300) : textContent}
          </div>
        )}
      </div>
    </div>
  );
};

export const WorkerDynamicIsland: React.FC = () => {
  const workerStreams = useAgentTeamStore(s => s.workerStreams);
  const [expanded, setExpanded] = useState(false);

  const workers = useMemo(
    () => Array.from(workerStreams.values()),
    [workerStreams]
  );

  const runningCount = workers.filter(w => w.status === 'running').length;
  const hasWorkers = workers.length > 0;

  useEffect(() => {
    if (runningCount > 0 && !expanded) {
      setExpanded(true);
    }
  }, [runningCount, expanded]);

  if (!hasWorkers) return null;

  const allDone = runningCount === 0;

  return (
    <div className="absolute right-3 top-3 z-20 flex flex-col items-end gap-2 max-w-[280px] transition-all duration-300">
      {/* Pill / collapsed indicator */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm
          border text-xs font-medium transition-all duration-200 cursor-pointer
          ${allDone
            ? 'bg-green-100/90 border-green-200 text-green-700'
            : 'bg-white/90 border-blue-200 text-blue-700'
          }
          hover:shadow-xl active:scale-95
        `}
      >
        {allDone ? (
          <>
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>{workers.length} 个任务已完成</span>
          </>
        ) : (
          <>
            <Zap className="h-3.5 w-3.5" />
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
            </span>
            <span>{runningCount} 个任务运行中</span>
          </>
        )}
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 opacity-50" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        )}
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="w-full flex flex-col gap-1.5 animate-in slide-in-from-top-2 duration-200">
          {workers.map(worker => (
            <WorkerStreamCard
              key={worker.taskId}
              worker={worker}
              isExpanded={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkerDynamicIsland;
